import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  FolderOutlined,
  InfoCircleOutlined,
  MinusOutlined
} from "@ant-design/icons"
import { Alert, App, Button, Skeleton } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { answerReply, getMessage } from "../api/messages.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import { EmailPreview } from "../EmailPreview.tsx"
import { MessageStatusDot, sourceOf } from "../hub/message-status.tsx"
import { ChannelLabel, ContactAvatar, SourceTag } from "../hub/ui.tsx"
import { useStamp } from "../hub/use-stamp.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { formatInZone } from "../composer/held-until.ts"
import HoldNotice from "../composer/HoldNotice.tsx"
import type {
  MessageDetail,
  QueueActionRecord,
  Reply
} from "../types/messages.types.ts"
import { QueueActions, useWhileMounted } from "./QueueActions.tsx"
import { offered } from "./queue-actions.ts"
import { recordLabel, recordLink } from "./record-link.ts"

const FAILED = new Set(["failed", "bounced", "complained"])

const HOLD_REASONS = new Set([
  "quiet_hours",
  "send_window",
  "legal_hours",
  "scheduled",
  "capped",
  "throttled",
  "retry",
  "paused"
])

export const isHeld = (message: MessageDetail) =>
  message.status === "queued" && HOLD_REASONS.has(message.hold_reason)

export interface TimelineStep {
  key: "queued" | "sent" | "delivered" | "failed" | "skipped" | "replied"
  at: string | null
  state: "done" | "pending" | "failed"
  label: string
}

/** Delivery history in the order it happened; a failed or skipped send ends the line. */
export const deliveryTimeline = (message: MessageDetail): TimelineStep[] => {
  const queued: TimelineStep = {
    key: "queued",
    at: message.created,
    state: "done",
    label: "queued"
  }
  if (message.status === "skipped")
    return [
      queued,
      {
        key: "skipped",
        at: message.skipped_at,
        state: "failed",
        label: "skipped"
      }
    ]
  const sent: TimelineStep = {
    key: "sent",
    at: message.sent_at,
    state: message.sent_at ? "done" : "pending",
    label: "sent"
  }
  if (FAILED.has(message.status))
    return [
      queued,
      sent,
      {
        key: "failed",
        at:
          message.failed_at ??
          message.bounced_at ??
          message.complained_at ??
          null,
        state: "failed",
        label: message.status
      }
    ]
  const steps: TimelineStep[] = [
    queued,
    sent,
    {
      key: "delivered",
      at: message.delivered_at,
      state: message.delivered_at ? "done" : "pending",
      label: "delivered"
    }
  ]
  if (message.first_replied_at)
    steps.push({
      key: "replied",
      at: message.first_replied_at,
      state: "done",
      label: "replied"
    })
  return steps
}

const StepMarker = ({ state }: { state: TimelineStep["state"] }) => (
  <span
    aria-hidden
    className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-white ${
      state === "done"
        ? "bg-[#2A7D8C]"
        : state === "failed"
          ? "bg-red-600"
          : "border border-solid border-slate-300 bg-surface text-slate-400"
    }`}
  >
    {state === "done" ? (
      <CheckOutlined />
    ) : state === "failed" ? (
      <CloseOutlined />
    ) : (
      <MinusOutlined />
    )}
  </span>
)

export const DeliveryTimeline = ({ message }: { message: MessageDetail }) => {
  const { t } = useTranslation()
  const { stamp } = useStamp()
  const steps = deliveryTimeline(message)
  return (
    <section aria-labelledby="delivery-timeline-title">
      <h3
        id="delivery-timeline-title"
        className="m-0 mb-3 text-sm font-semibold text-slate-900"
      >
        {t("commHub.messages.timeline")}
      </h3>
      <ol className="m-0 flex list-none flex-col gap-3 p-0 sm:flex-row sm:gap-0">
        {steps.map((step, index) => (
          <li
            key={step.key}
            className="flex flex-1 items-center gap-2 sm:flex-col sm:text-center"
          >
            <span className="flex w-full items-center">
              <span
                aria-hidden
                className={`hidden h-0.5 flex-1 sm:block ${
                  index === 0 ? "invisible" : "bg-slate-200"
                }`}
              />
              <StepMarker state={step.state} />
              <span
                aria-hidden
                className={`hidden h-0.5 flex-1 sm:block ${
                  index === steps.length - 1 ? "invisible" : "bg-slate-200"
                }`}
              />
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-medium text-slate-900">
                {t(`communication.messages.status.${step.label}`, {
                  defaultValue: step.label
                })}
              </span>
              <span className="text-xs text-slate-500">
                {step.at
                  ? stamp(step.at)
                  : step.state === "pending"
                    ? t("commHub.messages.notYet")
                    : ""}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** Who asked to send this message now or again, when, and with which override. */
export const QueueActionHistory = ({
  records
}: {
  records: QueueActionRecord[]
}) => {
  const { t } = useTranslation()
  const { stamp } = useStamp()
  if (records.length === 0) return null
  return (
    <section
      aria-labelledby="queue-action-history-title"
      className="mt-4 flex flex-col gap-2"
    >
      <h4
        id="queue-action-history-title"
        className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500"
      >
        {t("commHub.messages.actions.history.title")}
      </h4>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-slate-700">
        {records.map((record) => (
          <li key={`${record.action}:${record.at}`}>
            {t(`commHub.messages.actions.history.${record.action}`, {
              name: record.by ?? t("communication.messages.automated"),
              time: stamp(record.at)
            })}
            {record.override.length > 0 && (
              <span className="text-amber-800">
                {" "}
                {t("commHub.messages.actions.history.override", {
                  rules: record.override
                    .map((code) =>
                      t(`commHub.messages.actions.overrideRule.${code}`)
                    )
                    .join(t("commHub.messages.actions.and"))
                })}
              </span>
            )}
            {record.confirmed_not_received && (
              <span className="text-amber-800">
                {" "}
                {t("commHub.messages.actions.history.confirmedNotReceived")}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

const RepliesSection = ({
  message,
  onAnswered
}: {
  message: MessageDetail
  onAnswered: () => void
}) => {
  const { t } = useTranslation()
  const { message: toast } = App.useApp()
  const { stamp } = useStamp()
  const rights = useCommunicationRights()
  const [answering, setAnswering] = useState<string | null>(null)
  const [answer, setAnswer] = useState("")
  const [sending, setSending] = useState(false)

  const sendAnswer = async (reply: Reply) => {
    if (sending) return
    setSending(true)
    const response = await answerReply(reply.id, answer.trim())
    setSending(false)
    if (!response.ok) {
      const detail = (
        response.data as { details?: { detail?: unknown } } | undefined
      )?.details?.detail
      toast.error(
        typeof detail === "string"
          ? detail
          : t("communication.messages.detail.answerFailed")
      )
      return
    }
    setAnswering(null)
    toast.success(t("communication.messages.detail.answerSent"))
    onAnswered()
  }

  if (message.replies.length === 0) return null
  return (
    <section aria-labelledby="replies-title" className="flex flex-col gap-3">
      <h3
        id="replies-title"
        className="m-0 text-sm font-semibold text-slate-900"
      >
        {t("communication.messages.detail.repliesTitle", {
          count: message.replies.length
        })}
      </h3>
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {message.replies.map((reply) => (
          <li key={reply.id} className="rounded-lg bg-slate-50 p-3">
            <p className="m-0 mb-1 text-sm">
              <span className="font-semibold">
                {reply.from_name || reply.from_address}
              </span>{" "}
              <span className="text-slate-500">
                · {stamp(reply.received_at)}
              </span>
            </p>
            <p className="m-0 whitespace-pre-wrap text-sm">{reply.body_text}</p>
            {(reply.answers ?? []).length > 0 && (
              <ul
                aria-label={t("communication.messages.detail.answers")}
                className="mt-2 flex list-none flex-col gap-2 border-0 border-l-2 border-solid border-slate-200 pl-3"
              >
                {(reply.answers ?? []).map((sent) => (
                  <li key={sent.id} className="text-sm">
                    <span className="text-xs text-slate-500">
                      {t("communication.messages.detail.answeredBy", {
                        name:
                          sent.sender_name ||
                          t("communication.messages.detail.yourCompany"),
                        date: stamp(sent.sent_at ?? sent.created)
                      })}
                    </span>
                    <p className="m-0 whitespace-pre-wrap">{sent.body_text}</p>
                  </li>
                ))}
              </ul>
            )}
            {rights.send && answering !== reply.id && (
              <Button
                size="small"
                className="mt-2"
                onClick={() => {
                  setAnswer("")
                  setAnswering(reply.id)
                }}
              >
                {t("communication.messages.detail.reply")}
              </Button>
            )}
            {answering === reply.id && (
              <div className="mt-2 flex flex-col gap-2">
                <span className="text-xs text-slate-500">
                  {t("communication.messages.detail.answerTo", {
                    address: reply.from_address
                  })}
                </span>
                <TextArea
                  autoSize={{ minRows: 3 }}
                  maxLength={reply.channel === "sms" ? 1600 : 10000}
                  aria-label={t("communication.messages.detail.answerLabel", {
                    name: reply.from_name || reply.from_address
                  })}
                  value={answer}
                  onChange={(event) => setAnswer(event.target.value)}
                />
                <span className="flex gap-2">
                  <Button
                    type="primary"
                    size="small"
                    loading={sending}
                    disabled={!answer.trim()}
                    onClick={() => void sendAnswer(reply)}
                  >
                    {t("communication.messages.detail.sendAnswer")}
                  </Button>
                  <Button size="small" onClick={() => setAnswering(null)}>
                    {t("commHub.wizard.cancel")}
                  </Button>
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

export const MessageReadingPane = ({
  messageId,
  onNewMessage,
  onLoaded,
  afterRecipients,
  afterBody,
  queueActions = true,
  onQueueAction,
  refreshToken
}: {
  messageId: string
  onNewMessage?: (message: MessageDetail) => void
  onLoaded?: (message: MessageDetail) => void
  /** Rendered under the sender, recipients and record details. */
  afterRecipients?: React.ReactNode
  /** Rendered under the message body, above the delivery timeline. */
  afterBody?: React.ReactNode
  /** Offer Send now on a waiting message and Retry on a failed one (default). */
  queueActions?: boolean
  /** After Send now or Retry, with the message as it now is: for the host to refresh its list. */
  onQueueAction?: (message: MessageDetail) => void
  /** Bumped by the host when it knows the message changed; the pane reads it again in place. */
  refreshToken?: number
}) => {
  const { t, i18n } = useTranslation()
  const { stamp } = useStamp()
  const accountZone = useAppSelector(
    (state) => state.auth?.userInfo?.timezone as string | undefined
  )
  const [message, setMessage] = useState<MessageDetail | null>(null)
  const [failed, setFailed] = useState<false | "missing" | "error">(false)

  const load = useCallback(
    (isCurrent: () => boolean = () => true) =>
      getMessage(messageId).then((response) => {
        if (!isCurrent()) return
        if (response.ok && response.data) {
          setMessage(response.data)
          onLoaded?.(response.data)
        } else setFailed(response.status === 404 ? "missing" : "error")
      }),
    [messageId, onLoaded]
  )

  useEffect(() => {
    setMessage(null)
    setFailed(false)
    let current = true
    void load(() => current)
    return () => {
      current = false
    }
  }, [load])

  const firstToken = React.useRef(refreshToken)
  useEffect(() => {
    if (refreshToken === firstToken.current) return
    let current = true
    void load(() => current)
    return () => {
      current = false
    }
  }, [refreshToken, load])

  const afterQueueAction = useWhileMounted((changed: MessageDetail) => {
    setMessage(changed)
    onQueueAction?.(changed)
  })

  if (failed === "missing")
    return (
      <Alert type="info" showIcon message={t("commHub.messages.unavailable")} />
    )
  if (failed)
    return (
      <Alert
        type="error"
        showIcon
        message={t("communication.messages.detail.loadFailed")}
        action={
          <Button size="small" onClick={() => void load()}>
            {t("commHub.common.retry")}
          </Button>
        }
      />
    )
  if (!message) return <Skeleton active paragraph={{ rows: 10 }} />

  const link = recordLink(message.record)
  const label = message.record ? recordLabel(message.record) : ""
  const source = sourceOf(message)
  const recipientName = message.contact_name || message.to_address
  const available = queueActions ? offered(message) : []
  const actionControl =
    available.length > 0 ? (
      <QueueActions message={message} onDone={afterQueueAction} size="middle" />
    ) : undefined
  const sendNowOffered = available.includes("send_now")

  return (
    <article className="flex flex-col gap-5" aria-labelledby="reading-subject">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h2
            id="reading-subject"
            className="m-0 text-xl font-semibold text-slate-900"
          >
            {message.subject || t("commHub.messages.textMessage")}
          </h2>
          <MessageStatusDot status={message.status} pill />
        </div>
        <span className="text-sm text-slate-500">
          {stamp(message.sent_at ?? message.created)}
        </span>
      </header>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ContactAvatar
            name={message.from_name || message.sender_name || "?"}
            seed={message.sender ?? message.from_address}
            size={44}
          />
          <div className="flex flex-col text-sm">
            <span className="font-semibold text-slate-900">
              {message.from_name ||
                message.sender_name ||
                t("communication.messages.automated")}
              {message.from_address && (
                <span className="font-normal text-slate-500">
                  {" "}
                  &lt;{message.from_address}&gt;
                </span>
              )}
            </span>
            <span className="text-slate-600">
              {t("commHub.messages.toLine", {
                name: recipientName,
                address:
                  message.contact_name && message.to_address
                    ? ` <${message.to_address}>`
                    : ""
              })}
            </span>
          </div>
        </div>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          {label && (
            <>
              <dt className="text-slate-500">
                {t("commHub.messages.relatedTo")}
              </dt>
              <dd className="m-0">
                {link ? (
                  <Link to={link} className="inline-flex items-center gap-1">
                    <FolderOutlined aria-hidden />
                    {label}
                  </Link>
                ) : (
                  label
                )}
              </dd>
            </>
          )}
          <dt className="text-slate-500">{t("commHub.columns.source")}</dt>
          <dd className="m-0 flex flex-wrap items-center gap-2">
            <SourceTag source={source} />
            <ChannelLabel channel={message.channel} />
            {message.campaign && (
              <Link
                to={`${RoutesPaths.communication.campaigns}/${message.campaign}`}
              >
                {message.campaign_subject || t("commHub.messages.openCampaign")}
              </Link>
            )}
            {message.automation && (
              <Link
                to={`${RoutesPaths.communication.automations}?edit=${message.automation.id}`}
              >
                {message.automation.name}
              </Link>
            )}
          </dd>
          <>
            <dt className="text-slate-500">
              {t("communication.messages.detail.sentBy")}
            </dt>
            <dd className="m-0">
              {message.sender_name ?? t("communication.messages.automated")}
            </dd>
          </>
        </dl>
      </div>
      {afterRecipients}

      {isHeld(message) && (
        <Alert
          type="info"
          showIcon
          action={sendNowOffered ? actionControl : undefined}
          message={
            <>
              {t(`communication.messages.hold.${message.hold_reason}`)}{" "}
              {message.hold_zone ? (
                <HoldNotice
                  hold={message}
                  accountZone={accountZone}
                  className="inline"
                />
              ) : (
                message.not_before &&
                t("communication.messages.hold.at", {
                  time:
                    formatInZone(
                      message.not_before,
                      accountZone,
                      i18n.language || "en"
                    ) ?? stamp(message.not_before)
                })
              )}
            </>
          }
        />
      )}
      {sendNowOffered && !isHeld(message) && (
        <Alert
          type="info"
          showIcon
          action={actionControl}
          message={
            message.not_before
              ? t("commHub.messages.actions.waitingUntil", {
                  time:
                    formatInZone(
                      message.not_before,
                      accountZone,
                      i18n.language || "en"
                    ) ?? stamp(message.not_before),
                  zone: accountZone ?? ""
                })
              : t("commHub.messages.actions.waitingForWorker")
          }
        />
      )}
      {message.exempt && (
        <p className="m-0 text-xs text-slate-500">
          {t(`communication.holdNotice.exempt.${message.exempt}`)}
        </p>
      )}
      {(message.failure_reason || message.status_reason) &&
        (FAILED.has(message.status) || message.status === "skipped") && (
          <Alert
            type={message.status === "skipped" ? "warning" : "error"}
            showIcon
            action={available.includes("retry") ? actionControl : undefined}
            message={t(
              message.status === "skipped"
                ? "commHub.messages.notSentTitle"
                : "commHub.messages.failedTitle"
            )}
            description={
              message.failure_reason ||
              t(`communication.composer.reasons.${message.status_reason}`, {
                defaultValue: message.status_reason
              })
            }
          />
        )}

      <section
        aria-label={t("communication.messages.detail.body")}
        className="border-0 border-t border-solid border-slate-100 pt-4"
      >
        {message.channel === "email" && message.body_html ? (
          <EmailPreview
            html={message.body_html}
            title={t("communication.messages.detail.body")}
          />
        ) : (
          <p className="m-0 max-w-xl whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3 text-sm">
            {message.body_text}
          </p>
        )}
      </section>
      {afterBody}

      <div className="border-0 border-t border-solid border-slate-100 pt-4">
        <DeliveryTimeline message={message} />
        <QueueActionHistory records={message.queue_actions ?? []} />
      </div>

      <RepliesSection message={message} onAnswered={() => void load()} />

      <footer className="flex flex-wrap items-center justify-between gap-3 border-0 border-t border-solid border-slate-100 pt-4">
        {onNewMessage &&
        (message.customer ||
          message.lead ||
          (!message.contact_kind &&
            message.channel === "email" &&
            message.to_address)) ? (
          <Button
            type="primary"
            size="large"
            icon={<EditOutlined aria-hidden />}
            onClick={() => onNewMessage(message)}
          >
            {t("commHub.messages.newTo", { name: recipientName })}
          </Button>
        ) : (
          <span />
        )}
        <span className="flex max-w-md items-start gap-2 text-xs text-slate-600">
          <InfoCircleOutlined aria-hidden className="mt-0.5" />
          {t(
            message.channel === "sms"
              ? "commHub.messages.replyNoteSms"
              : "commHub.messages.replyNoteEmail"
          )}
        </span>
      </footer>
    </article>
  )
}
